<template>
  <RouterLink
    v-if="isActive"
    class="nav-link"
    :to="props.link"
  >
    <div class="nav-container" :style="{ background: props.color || '#FFF' }">
      <span class="nav-container__icon">{{ props.icon }}</span>
      <span class="nav-container__body">
        <strong class="nav-container__text" v-html="title"></strong>
        <span v-if="props.description" class="nav-container__description">
          {{ props.description }}
        </span>
      </span>
    </div>
  </RouterLink>

  <div
    v-else
    class="nav-container nav-container--disabled"
    aria-disabled="true"
  >
    <span class="nav-container__icon">{{ props.icon }}</span>
    <span class="nav-container__body">
      <strong class="nav-container__text" v-html="title"></strong>
      <span v-if="props.description" class="nav-container__description">
        {{ props.description }}
      </span>
    </span>
    <span class="nav-container__badge">준비중</span>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps } from 'vue';
import { RouterLink } from 'vue-router';

const props = defineProps({
  link: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '•',
  },
  color: {
    type: String,
    default: '#FFF',
  },
});

const isActive = computed(() => Boolean(props.link));
</script>

<style lang="scss" scoped>
.nav-link {
  display: block;
  height: 100%;
  color: inherit;
  text-decoration: none;
}

.nav-container {
  position: relative;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 86px;
  height: 100%;
  border-radius: 8px;
  border: 1px solid #d5e0e5;
  padding: 14px;
  box-sizing: border-box;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(35, 49, 61, 0.06);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;

  &:hover {
    border-color: #8fbfd0;
    box-shadow: 0 12px 22px rgba(35, 49, 61, 0.11);
    transform: translateY(-1px);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #eaf3f6;
    color: #1d6f86;
    font-size: 12px;
    font-weight: 900;
  }

  &__body {
    display: grid;
    gap: 5px;
    min-width: 0;
  }

  &__text {
    color: #20313c;
    font-size: 14px;
    font-weight: 800;
    line-height: 1.25;
  }

  &__description {
    color: #6b7a83;
    font-size: 12px;
    line-height: 1.35;
  }

  &__badge {
    position: absolute;
    right: 8px;
    bottom: 7px;
    padding: 2px 6px;
    border-radius: 999px;
    background: #e0e4e7;
    color: #7b858c;
    font-size: 10px;
    font-weight: 800;
  }

  &--disabled {
    background: #edf0f2;
    border-color: #d8dde1;
    box-shadow: none;
    cursor: not-allowed;

    &:hover {
      border-color: #d8dde1;
      box-shadow: none;
      transform: none;
    }

    .nav-container__icon {
      background: #dfe4e7;
      color: #8b949b;
    }

    .nav-container__text,
    .nav-container__description {
      color: #8b949b;
    }
  }
}

@media (max-width: 720px) {
  .nav-container {
    min-height: 74px;
    grid-template-columns: 36px minmax(0, 1fr);
    gap: 10px;
    padding: 12px;
  }

  .nav-container__icon {
    width: 36px;
    height: 36px;
    font-size: 11px;
  }

  .nav-container__text {
    font-size: 14px;
    line-height: 1.2;
  }

  .nav-container__description {
    font-size: 12px;
    line-height: 1.25;
  }
}
</style>
